import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormFullWidth,
  FormTextField,
  FormSelect,
  FormCheckboxGroup,
  FormMessage,
  checkboxSx,
  textFieldSx,
} from '../../Components/Form';
import apiClient from '../../Services/ApiClient';
import useGetCustomData from '../../Hooks/useGetCustomData';
import {
  DAY_LABELS,
  GROUP_MODE_LABELS,
  LocationOption,
  PreviewResult,
  Recipient,
  SORT_LABELS,
  SOURCE_LABELS,
  SortBy,
  GroupMode,
  REPORT_TIMEZONE,
  UsageReportFormValues,
  UsageReportSchedule,
  describeRange,
  formatClock,
} from './usageReportTypes';

const DEFAULT_VALUES: UsageReportFormValues = {
  name: '',
  ovid: '',
  active_flag: 1,
  timezone: REPORT_TIMEZONE,
  run_time: '06:00',
  run_days: [1, 2, 3, 4, 5],
  range_start_time: '06:00',
  range_end_time: '06:00',
  group_mode: 'summary',
  sort_by: 'time',
  replenish_enabled: 0,
  skip_if_empty: 0,
  recipients: [],
};

const fromSchedule = (s: UsageReportSchedule): UsageReportFormValues => ({
  name: s.name,
  ovid: s.ovid,
  active_flag: s.active_flag,
  timezone: s.timezone,
  run_time: s.run_time,
  run_days: s.run_days,
  range_start_time: s.range_start_time,
  range_end_time: s.range_end_time,
  group_mode: s.group_mode,
  sort_by: s.sort_by,
  replenish_enabled: s.replenish_enabled,
  skip_if_empty: s.skip_if_empty,
  recipients: s.recipients,
});

export const toPayload = (v: UsageReportFormValues) => ({
  name: v.name.trim(),
  ovid: v.ovid,
  active_flag: v.active_flag,
  timezone: v.timezone,
  run_time: v.run_time,
  run_days: v.run_days,
  range_start_time: v.range_start_time,
  range_end_time: v.range_end_time,
  group_mode: v.group_mode,
  sort_by: v.sort_by,
  replenish_enabled: v.replenish_enabled,
  skip_if_empty: v.skip_if_empty,
  recipient_user_ids: v.recipients.map((r) => r.user_id),
});

const formatPreviewTime = (value: string) => {
  const [date, time] = value.split(' ');
  const [y, m, d] = date.split('-');
  return `${m}/${d}/${y} ${formatClock(time.slice(0, 5))}`;
};

interface UsageReportFormProps {
  schedule: UsageReportSchedule | null;
  onCancel: () => void;
  onSaved: () => void;
}

const UsageReportForm: React.FC<UsageReportFormProps> = ({ schedule, onCancel, onSaved }) => {
  const [values, setValues] = useState<UsageReportFormValues>(schedule ? fromSchedule(schedule) : DEFAULT_VALUES);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [locations] = useGetCustomData('/api/usage-reports/options/locations');
  const locationOptions: LocationOption[] = Array.isArray(locations) ? locations : [];

  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientOptions, setRecipientOptions] = useState<Recipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // Which run day to preview (UTC). Defaults to today; pick an earlier day to check past activity.
  const [previewDate, setPreviewDate] = useState(() => new Date().toISOString().slice(0, 10));

  const set = <K extends keyof UsageReportFormValues>(key: K, value: UsageReportFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  // Recipient search, debounced; users assigned to the chosen location come first.
  useEffect(() => {
    const handle = setTimeout(async () => {
      setRecipientsLoading(true);
      try {
        const params = new URLSearchParams();
        if (recipientSearch.trim()) params.set('search', recipientSearch.trim());
        if (values.ovid) params.set('ovid', String(values.ovid));
        const res = await apiClient(`/api/usage-reports/options/recipients?${params.toString()}`);
        if (res.ok) setRecipientOptions(await res.json());
      } finally {
        setRecipientsLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [recipientSearch, values.ovid]);

  const selectedLocation = useMemo(
    () => locationOptions.find((l) => l.ovid === values.ovid) ?? null,
    [locationOptions, values.ovid],
  );

  const handleDayToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const day = Number(e.target.name);
    set('run_days', e.target.checked
      ? [...values.run_days, day].sort()
      : values.run_days.filter((d) => d !== day));
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError('');
    try {
      const res = await apiClient('/api/usage-reports/preview', {
        method: 'POST',
        body: JSON.stringify({ ...toPayload(values), preview_date: previewDate }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message ?? 'Preview failed');
        setPreview(null);
        return;
      }
      setPreview(data);
    } catch {
      setError('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) { setError('Report name is required'); return; }
    if (!values.ovid) { setError('Select a location'); return; }
    if (values.run_days.length === 0) { setError('Select at least one day for the report to run'); return; }
    if (values.recipients.length === 0) { setError('Add at least one recipient'); return; }

    setSaving(true);
    try {
      const res = await apiClient(schedule ? `/api/usage-reports/${schedule.id}` : '/api/usage-reports', {
        method: schedule ? 'PUT' : 'POST',
        body: JSON.stringify(toPayload(values)),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message ?? 'Failed to save');
        return;
      }
      onSaved();
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isSummary = values.group_mode === 'summary';

  return (
    <Form
      embedded
      onSubmit={handleSubmit}
      title={schedule ? 'Edit Usage Report' : 'New Usage Report'}
      subtitle="Emails a location's Transactions usage to selected users on a schedule."
      submitLabel={saving ? 'Saving...' : 'Save'}
      onCancel={onCancel}
      isLoading={saving}
    >
      <FormSection>
        <SectionTitle>Report</SectionTitle>
        <FormGrid>
          <FormTextField
            label="Report Name"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            inputProps={{ maxLength: 150 }}
            placeholder="e.g. Dry Creek — Daily Usage"
            required
          />
          <Autocomplete
            options={locationOptions}
            value={selectedLocation}
            onChange={(_, option) => set('ovid', option ? option.ovid : '')}
            getOptionLabel={(o) => `${o.name}${o.customer_name ? ` — ${o.customer_name}` : ''}`}
            isOptionEqualToValue={(a, b) => a.ovid === b.ovid}
            renderInput={(params) => (
              <TextField {...params} label="Location" size="small" required sx={textFieldSx} />
            )}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>When it runs</SectionTitle>
        <SectionDescription>All times are UTC.</SectionDescription>
        <FormGrid $columns={2}>
          <FormTextField
            label="Run Time (UTC)"
            type="time"
            value={values.run_time}
            onChange={(e) => set('run_time', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </FormGrid>
        <FormCheckboxGroup
          columns={3}
          onChange={handleDayToggle}
          options={DAY_LABELS.map((label, day) => ({
            name: String(day),
            label,
            checked: values.run_days.includes(day),
          }))}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>Time range to pull</SectionTitle>
        <FormGrid $columns={2}>
          <FormTextField
            label="Pull From (UTC)"
            type="time"
            value={values.range_start_time}
            onChange={(e) => set('range_start_time', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
          <FormTextField
            label="Pull To (UTC)"
            type="time"
            value={values.range_end_time}
            onChange={(e) => set('range_end_time', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </FormGrid>
        <SectionDescription>
          {describeRange(values)} Each email covers the latest period that has ended by its send time —
          use the same time in both for the last 24 hours.
        </SectionDescription>
      </FormSection>

      <FormSection>
        <SectionTitle>Layout</SectionTitle>
        <FormGrid $columns={2}>
          <FormSelect
            label="Rows"
            value={values.group_mode}
            onChange={(e) => set('group_mode', e.target.value as GroupMode)}
            options={(Object.keys(GROUP_MODE_LABELS) as GroupMode[]).map((k) => ({ value: k, label: GROUP_MODE_LABELS[k] }))}
            helperText={isSummary
              ? 'Repeat transactions of the same item by the same user, bin and PO/Job are combined into one total.'
              : 'Each transaction is its own line.'}
          />
          <FormSelect
            label="Sort By"
            value={values.sort_by}
            onChange={(e) => set('sort_by', e.target.value as SortBy)}
            options={(Object.keys(SORT_LABELS) as SortBy[]).map((k) => ({ value: k, label: SORT_LABELS[k] }))}
            helperText={values.sort_by === 'time' ? 'Oldest first.' : 'Rows are grouped under a heading with a subtotal.'}
          />
          <FormFullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!values.replenish_enabled}
                  onChange={(e) => set('replenish_enabled', e.target.checked ? 1 : 0)}
                  size="small"
                  sx={checkboxSx}
                />
              }
              label="Show Replenish buttons: one next to each total, plus a Replenish All in Bin button per bin (moves the used quantity from the Primary Bin back into the bin it was taken from)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!values.skip_if_empty}
                  onChange={(e) => set('skip_if_empty', e.target.checked ? 1 : 0)}
                  size="small"
                  sx={checkboxSx}
                />
              }
              label="Don't send when there were no transactions"
            />
          </FormFullWidth>
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Recipients</SectionTitle>
        <Autocomplete
          multiple
          options={recipientOptions}
          value={values.recipients}
          loading={recipientsLoading}
          filterOptions={(x) => x}
          onChange={(_, selected) => set('recipients', selected)}
          onInputChange={(_, text, reason) => { if (reason === 'input' || reason === 'clear') setRecipientSearch(text); }}
          getOptionLabel={(o) => `${o.name} <${o.email}>`}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
          groupBy={values.ovid ? (o) => (o.assigned ? 'Assigned to this location' : 'Other users') : undefined}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.user_id} size="small" label={option.name || option.email} title={option.email} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Email to"
              placeholder="Search users by name or email"
              sx={textFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {recipientsLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>Preview</SectionTitle>
        <SectionDescription>
          Runs the report with these settings as it would have run on the chosen day. Nothing is emailed.
        </SectionDescription>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormTextField
            label="Preview as of (UTC)"
            type="date"
            value={previewDate}
            onChange={(e) => { setPreviewDate(e.target.value); setPreview(null); }}
            InputLabelProps={{ shrink: true }}
            fullWidth={false}
            sx={{ width: 200 }}
          />
          <button
            type="button"
            onClick={handlePreview}
            disabled={!values.ovid || previewing}
            style={{
              background: 'transparent', border: '1px solid #0098aa', color: '#0098aa', borderRadius: 6,
              padding: '6px 14px', cursor: values.ovid ? 'pointer' : 'not-allowed', fontWeight: 600,
            }}
          >
            {previewing ? 'Loading...' : 'Preview Report'}
          </button>
        </div>
        {preview && preview.rows.length === 0 && (
          <FormMessage $type="info">
            No transactions at this location in that period. Try an earlier "Preview as of" date or a wider time range.
          </FormMessage>
        )}
        {preview && (
          <>
            <SectionDescription>
              {formatPreviewTime(preview.range_start.replace('T', ' ').slice(0, 19))} to{' '}
              {formatPreviewTime(preview.range_end.replace('T', ' ').slice(0, 19))} · {preview.rows.length} row
              {preview.rows.length === 1 ? '' : 's'} · {preview.total_quantity} total qty
            </SectionDescription>
            {preview.rows.length > 0 && (
              <TableContainer style={{ maxHeight: 320, border: '1px solid #e0e0e0', borderRadius: 6 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>{isSummary ? 'First – Last Transaction' : 'Date / Time'}</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Bin</TableCell>
                      <TableCell>PO/Job</TableCell>
                      {isSummary && <TableCell align="right"># of Transactions</TableCell>}
                      <TableCell align="right">Qty</TableCell>
                      {!!values.replenish_enabled && <TableCell align="center">Replenish</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((r) => (
                      <TableRow key={`${r.source}-${r.tids.join('-')}`}>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>{SOURCE_LABELS[r.source] ?? 'ABATIX'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatPreviewTime(r.first_at)}
                          {isSummary && r.last_at !== r.first_at ? ` – ${formatPreviewTime(r.last_at)}` : ''}
                        </TableCell>
                        <TableCell>{r.product_code}</TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell>{r.user_name}</TableCell>
                        <TableCell>{r.bin_name}</TableCell>
                        <TableCell>{r.po_job_number || '—'}</TableCell>
                        {isSummary && <TableCell align="right">{r.transaction_count}</TableCell>}
                        <TableCell align="right"><strong>{r.quantity}</strong></TableCell>
                        {!!values.replenish_enabled && (
                          <TableCell align="center">{r.replenishable ? 'Button' : '—'}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </FormSection>

      {error && <FormMessage $type="error">{error}</FormMessage>}
    </Form>
  );
};

export default UsageReportForm;
